import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import { api } from "../lib/api";
import { Search, Star } from "lucide-react";

export default function Courses() {
  const [courses, setCourses] = useState([]);
  const [q, setQ] = useState("");
  const [level, setLevel] = useState("all");
  const [tab, setTab] = useState("official"); // official | community | all

  useEffect(() => {
    const query = tab === "all" ? "" : `?type=${tab}`;
    api.get(`/courses${query}`).then(({ data }) => setCourses(data));
  }, [tab]);

  const filtered = useMemo(() => {
    return courses.filter((c) => {
      const matchesQ =
        !q ||
        c.title.toLowerCase().includes(q.toLowerCase()) ||
        c.language.toLowerCase().includes(q.toLowerCase());
      const matchesLevel = level === "all" || (c.level || "").toLowerCase() === level;
      return matchesQ && matchesLevel;
    });
  }, [courses, q, level]);

  const levels = ["all", "beginner", "intermediate", "advanced"];
  const tabs = [
    { id: "official", label: "Official" },
    { id: "community", label: "Community" },
    { id: "all", label: "All" },
  ];

  return (
    <div className="min-h-screen grain-bg" data-testid="courses-page">
      <Nav />
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-10">
        <div className="mb-6">
          <h1 className="font-display text-4xl md:text-5xl font-bold text-brand-navy">Explore courses</h1>
          <p className="text-brand-navy/70 text-lg mt-2">Pick a language and dive in.</p>
        </div>

        <div className="inline-flex mb-6 border-2 border-brand-ink rounded-full bg-white shadow-brutal p-1">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`courses-tab-${t.id}`}
              className={`px-5 py-2 rounded-full font-bold text-sm ${tab === t.id ? "bg-brand-yellow" : ""}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="brutal-card-sm p-4 md:p-5 bg-white mb-8 flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-brand-navy/50" strokeWidth={3} />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              data-testid="courses-search"
              placeholder="Search language or course..."
              className="brutal-input pl-11"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {levels.map((l) => (
              <button
                key={l}
                onClick={() => setLevel(l)}
                data-testid={`courses-filter-${l}`}
                className={`brutal-badge capitalize ${
                  level === l ? "bg-brand-yellow" : "bg-white hover:bg-brand-peach"
                }`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => (
            <Link
              key={c.id}
              to={`/courses/${c.id}`}
              data-testid={`course-card-${c.id}`}
              className="brutal-card overflow-hidden hover:-translate-y-1 transition-transform"
            >
              <div className="relative h-40 border-b-2 border-brand-ink" style={{ backgroundColor: c.color || "#FFD166" }}>
                {c.image && (
                  <img src={c.image} alt={c.title} className="w-full h-full object-cover opacity-90" />
                )}
                <div className="absolute top-3 left-3 w-14 h-14 rounded-full bg-white border-2 border-brand-ink shadow-brutal flex items-center justify-center text-3xl">
                  {c.flag}
                </div>
                <div className="absolute top-3 right-3 flex flex-col items-end gap-2">
                  {c.is_official && (
                    <div className="brutal-badge bg-brand-yellow text-xs" data-testid="course-official-badge">
                      <Star className="w-3 h-3" strokeWidth={3} /> Official
                    </div>
                  )}
                  <div className="brutal-badge bg-white text-xs">{c.level}</div>
                </div>
              </div>
              <div className="p-5">
                <div className="font-display text-2xl font-bold text-brand-navy">{c.title}</div>
                <p className="text-sm text-brand-navy/70 mt-1 line-clamp-2">{c.description}</p>
                <div className="mt-3 flex items-center justify-between text-xs font-bold text-brand-navy/60">
                  <span>{c.card_count} cards</span>
                  <span>{c.language}</span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        {filtered.length === 0 && (
          <div className="brutal-card p-12 text-center bg-white">
            <p className="text-brand-navy/70">
              {tab === "community"
                ? "No community courses yet — be the first to create one!"
                : "No courses match your search."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
